import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, Users, DollarSign, AlertTriangle, TrendingUp, Calendar, Loader2 } from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';
import { useApi } from '../hooks/useApi';
import { enhancedAPI } from '../services/api';
import ForcePasswordChangeModal from '../components/ForcePasswordChangeModal';

const Dashboard = () => {
  const { data: dashboardData, loading, error, refetch } = useApi(() => enhancedAPI.getDashboardData());
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Check if user needs to change password
  useEffect(() => {
    const savedUser = localStorage.getItem('landlord_user');
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      if (userData.must_change_password) {
        setShowPasswordModal(true);
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-red-600" />
          <p className="text-red-600 mb-4">Failed to load dashboard data</p>
          <button 
            onClick={refetch}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { stats, properties, payments, maintenanceRequests } = dashboardData;
  
  // Calculate additional metrics
  const totalRentExpected = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const totalRentCollected = payments
    .filter(payment => payment.status === 'paid')
    .reduce((sum, payment) => sum + payment.amount, 0);
  const collectionRate = totalRentExpected > 0 ? ((totalRentCollected / totalRentExpected) * 100).toFixed(1) : 0;
  
  const overduePayments = payments.filter(payment => payment.status === 'overdue').length;
  const pendingMaintenance = maintenanceRequests.filter(req => req.status === 'pending').length;

  const statsCards = [
    {
      title: 'Total Properties',
      value: stats.total_properties,
      icon: Building2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Occupancy Rate',
      value: `${stats.occupancy_rate.toFixed(1)}%`,
      subtitle: `${stats.occupied_units}/${stats.total_units} units`,
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Monthly Income',
      value: formatCurrency(stats.monthly_income),
      subtitle: `${collectionRate}% collected`,
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      title: 'Issues',
      value: overduePayments + stats.pending_maintenance,
      subtitle: `${overduePayments} overdue, ${stats.pending_maintenance} work orders`,
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    }
  ];

  const recentPayments = payments.slice(0, 5);
  const recentMaintenance = maintenanceRequests.slice(0, 3);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Welcome back! Here's what's happening with your properties.</p>
        </div>
        <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500">
          <Calendar className="h-4 w-4" />
          <span className="hidden sm:inline">Last updated: {formatDate(new Date().toISOString())}</span>
          <span className="sm:hidden">Updated: {new Date().toLocaleDateString()}</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {statsCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{stat.title}</p>
                    <p className="text-lg sm:text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                    {stat.subtitle && (
                      <p className="text-xs text-gray-500 mt-1 hidden sm:block">{stat.subtitle}</p>
                    )}
                  </div>
                  <div className={`p-2 sm:p-3 ${stat.bgColor} rounded-lg flex-shrink-0 ml-2`}>
                    <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Payments */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Recent Payments</CardTitle>
            <TrendingUp className="h-5 w-5 text-gray-400" />
          </CardHeader>
          <CardContent className="space-y-4">
            {recentPayments.map((payment) => (
              <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{payment.tenantName}</p>
                  <p className="text-sm text-gray-600">{payment.propertyName} - {payment.unitNumber}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{formatCurrency(payment.amount)}</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    payment.status === 'paid' ? 'bg-green-100 text-green-800' :
                    payment.status === 'overdue' ? 'bg-red-100 text-red-800' : 
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {payment.status}
                  </span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent Maintenance */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Work Orders</CardTitle>
            <AlertTriangle className="h-5 w-5 text-gray-400" />
          </CardHeader>
          <CardContent className="space-y-4">
            {recentMaintenance.map((request) => (
              <div key={request.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{request.title}</p>
                  <p className="text-sm text-gray-600">{request.propertyName} - {request.unitNumber}</p>
                </div>
                <div className="text-right">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    request.status === 'completed' ? 'bg-green-100 text-green-800' :
                    request.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {request.status.replace('_', ' ')}
                  </span>
                  <div className={`mt-1 inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    request.priority === 'high' ? 'bg-red-100 text-red-800' :
                    request.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {request.priority}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Properties Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Properties Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {properties.map((property) => (
              <div key={property.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{property.name}</h3>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    {property.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{property.address}</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Units:</span>
                    <span className="ml-1 font-medium">{property.occupied_units}/{property.units}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Income:</span>
                    <span className="ml-1 font-medium">{formatCurrency(property.monthly_income)}</span>
                  </div>
                </div>
                <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{width: `${(property.occupied_units / property.units) * 100}%`}}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Force Password Change Modal */}
      {showPasswordModal && (
        <ForcePasswordChangeModal
          onSuccess={() => {
            setShowPasswordModal(false);
            refetch(); // Refresh dashboard after password change
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;